import { zodResolver } from '@hookform/resolvers/zod';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useDuplicateTourMutation } from '#api/tourApi.slice.ts';
import { usePostSubscriptionMutation } from '#api-bridge/notificationsApi.ts';
import { createTourSubscriptions } from '#components/tour/createTourSubscriptions.ts';
import type { Order } from '#domain/Order.ts';
import type { TourDetails } from '#domain/Tour.ts';
import { useAppNavigate } from '#routes/AppNavLink.tsx';
import { routes } from '#routes/routes.ts';

import { useDuplicateTourStops } from '../hooks/useDuplicateTourStops.ts';
import {
    getInitialValues,
    TourDuplicateFormValidation,
    type TourDuplicateFormValues,
    toDuplicateTourPayload,
} from '../model/TourDuplicateFormState.ts';
import { assertValidDuplicateTourPayload } from '../utils/DuplicateTourPayloadValidation.ts';
import { getSelectedOrderDetails } from '../utils/getSelectedOrderDetails.ts';
import { TourDuplicateDialog } from './components/TourDuplicateDialog.tsx';
import { TourDuplicateSortStopsDialog } from './components/TourDuplicateSortStopsDialog.tsx';

type TourDuplicateDialogsProps = {
    isOpen: boolean;
    selectedTour: TourDetails;
    orders: Order[];
    isLoadingOrders: boolean;
    isOrderError: boolean;
    onClose: () => void;
};

export const TourDuplicateDialogs = ({
    isOpen,
    selectedTour,
    orders,
    isLoadingOrders,
    isOrderError,
    onClose,
}: TourDuplicateDialogsProps) => {
    const intl = useIntl();
    const navigate = useAppNavigate();

    const [showStopOrderDialog, setShowStopOrderDialog] = useState(false);

    const formContext = useForm<TourDuplicateFormValues>({
        resolver: zodResolver(TourDuplicateFormValidation),
        mode: 'onTouched',
        defaultValues: getInitialValues(
            selectedTour,
            orders.map(o => o.id)
        ),
    });
    const { watch } = formContext;

    const [duplicateTour, { isLoading: isDuplicating }] = useDuplicateTourMutation();
    const [createSubscription, { isLoading: isCreatingSubscriptions }] = usePostSubscriptionMutation();
    const selectedOrderIds = watch('selectedOrderIds');
    const selectedOrders = useMemo(
        () => orders.filter(order => selectedOrderIds.includes(order.id)),
        [orders, selectedOrderIds]
    );
    const { sortableTourStops, setSortableTourStops, misplacedStopIds, isMisplaced } = useDuplicateTourStops(
        isOpen,
        selectedOrders,
        selectedTour
    );

    const handleDialogClose = () => {
        setShowStopOrderDialog(false);
        onClose();
    };

    const onSubmit = async (values: TourDuplicateFormValues) => {
        try {
            const selectedOrderDetails = getSelectedOrderDetails(orders, values.selectedOrderIds, selectedTour.stops);
            const payload = toDuplicateTourPayload(values, sortableTourStops, selectedOrderDetails);
            assertValidDuplicateTourPayload(payload);
            const createdTour = await duplicateTour(payload).unwrap();
            if (values.notificationsEnabled) {
                try {
                    await createSubscription(createTourSubscriptions(createdTour.id)).unwrap();
                } catch (error) {
                    console.error('Failed to create duplicated tour subscriptions', error);
                }
            }
            Notification.success(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.single.success',

                    defaultMessage: 'Tour duplicated successfully.',
                })
            );
            handleDialogClose();
            navigate(`${routes.TOURS}/${createdTour.id}`);
        } catch {
            Notification.error(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.single.failure',

                    defaultMessage: 'Tour could not be duplicated.',
                })
            );
        }
    };

    return (
        <>
            <TourDuplicateDialog
                formContext={formContext}
                isOpen={isOpen}
                selectedTour={selectedTour}
                onSubmit={onSubmit}
                setShowStopOrderDialog={setShowStopOrderDialog}
                orders={orders}
                isDuplicating={isDuplicating || isCreatingSubscriptions}
                isLoadingOrders={isLoadingOrders}
                isOrderError={isOrderError}
                handleDialogClose={handleDialogClose}
            />
            <TourDuplicateSortStopsDialog
                sortableTourStops={sortableTourStops}
                setSortableTourStops={setSortableTourStops}
                isMisplaced={isMisplaced}
                misplacedStopIds={misplacedStopIds}
                showStopOrderDialog={showStopOrderDialog}
                setShowStopOrderDialog={setShowStopOrderDialog}
            />
        </>
    );
};
